import React from "react";
import { applyGridToDom } from "../render/gridScale";
import { viewportTransform } from "../render/MapSvg";
import type { OverlayChannel } from "../render/overlayChannel";
import { SnapEngine } from "../snap/snapEngine";
import type { EditorStore } from "../store/createEditorStore";
import { parseGeometryHandle, resizeOpening } from "../tools/geometryGestures";
import { isPanGestureStart, panBy } from "../tools/panTool";
import type { Tool, ToolContext } from "../tools/Tool";
import type { Point, ToolKind } from "../types";
import { findShapeNode } from "./domTransform";
import { clientToWorld, isModifierKey } from "./pointer";

export const MIN_SCALE = 0.1;
export const MAX_SCALE = 10;
const WHEEL_COMMIT_DELAY = 100;
const NUDGE = 1;
const NUDGE_FAST = 10;

export interface CanvasGesturesOptions {
  store: EditorStore;
  overlay: OverlayChannel;
  tools: Partial<Record<ToolKind, Tool>>;
  svgRef: React.RefObject<SVGSVGElement | null>;
  rootRef: React.RefObject<SVGGElement | null>;
  containerRef: React.RefObject<HTMLDivElement | null>;
}

/**
 * One listener set on the `<svg>`: pointer capture on down, pan/zoom
 * handled here (they never touch React until they commit), everything else
 * forwarded to the active tool. Keyboard shortcuts on the container.
 */
export const useCanvasGestures = ({
  store,
  overlay,
  tools,
  svgRef,
  rootRef,
  containerRef
}: CanvasGesturesOptions): void => {
  React.useEffect(() => {
    const svg = svgRef.current;
    const container = containerRef.current;
    if (!svg || !container) return;

    const snap = new SnapEngine(store);
    let isSpaceDown = false;
    let pan: {
      client: Point;
      viewport: { x: number; y: number; scale: number };
    } | null = null;
    let wheelTimer: number | null = null;
    let liveViewport = store.getState().viewport;

    const context: ToolContext = {
      store,
      overlay,
      snap,
      toWorld: (client) =>
        clientToWorld(svg, store.getState().viewport, client),
      hitShape: (target) => {
        const element = target instanceof Element ? target : null;
        return (
          element?.closest<SVGGElement>("[data-shape-id]")?.dataset.shapeId ??
          null
        );
      },
      hitHandle: (target) => {
        const element = target instanceof Element ? target : null;
        return (
          element?.closest<SVGElement>("[data-handle]")?.dataset.handle ?? null
        );
      },
      shapeNode: (id) => findShapeNode(svg, id),
      elementAt: (client) => document.elementFromPoint(client.x, client.y),
      worldPerPixel: () => 1 / store.getState().viewport.scale,
      setCursor: (cursor) => {
        svg.style.cursor = cursor ?? activeTool()?.cursor ?? "default";
      }
    };

    const activeTool = (): Tool | undefined =>
      tools[store.getState().tool.kind];

    const applyViewport = (viewport: {
      x: number;
      y: number;
      scale: number;
    }) => {
      liveViewport = viewport;
      rootRef.current?.setAttribute("transform", viewportTransform(viewport));
      applyGridToDom(svg, store.getState().meta.gridSize, viewport.scale);
    };

    const commitViewport = () => {
      if (wheelTimer !== null) {
        window.clearTimeout(wheelTimer);
        wheelTimer = null;
      }
      store.getState().setViewport(liveViewport);
    };

    const onPointerDown = (event: PointerEvent) => {
      container.focus({ preventScroll: true });
      svg.setPointerCapture(event.pointerId);
      const toolKind = store.getState().tool.kind;
      if (isPanGestureStart(toolKind, event, isSpaceDown)) {
        event.preventDefault();
        pan = {
          client: { x: event.clientX, y: event.clientY },
          viewport: liveViewport
        };
        svg.style.cursor = "grabbing";
        return;
      }
      activeTool()?.onPointerDown(context, event);
    };

    // Hover: an attribute on the shape root (CSS draws the spot stroke)
    // and the id on the overlay channel (the outline). No React state.
    let hoveredNode: SVGGElement | null = null;
    const setHover = (id: string | null) => {
      const node = id ? findShapeNode(svg, id) : null;
      if (node === hoveredNode) return;
      hoveredNode?.removeAttribute("data-hovered");
      node?.setAttribute("data-hovered", "true");
      hoveredNode = node;
      overlay.set({ hoverId: id });
    };

    const onPointerMove = (event: PointerEvent) => {
      if (store.getState().tool.kind === "select" && event.buttons === 0) {
        const id = context.hitShape(event.target);
        const shape = id ? store.getState().shapes[id] : null;
        setHover(shape && !shape.locked ? id : null);
      } else if (hoveredNode) {
        setHover(null);
      }
      if (pan) {
        applyViewport(
          panBy(pan.viewport, {
            x: event.clientX - pan.client.x,
            y: event.clientY - pan.client.y
          })
        );
        return;
      }
      activeTool()?.onPointerMove(context, event);
    };

    const onPointerUp = (event: PointerEvent) => {
      if (svg.hasPointerCapture(event.pointerId)) {
        svg.releasePointerCapture(event.pointerId);
      }
      if (pan) {
        pan = null;
        commitViewport();
        context.setCursor(isSpaceDown ? "grab" : null);
        return;
      }
      activeTool()?.onPointerUp(context, event);
    };

    const onDoubleClick = (event: MouseEvent) => {
      activeTool()?.onDoubleClick?.(context, event);
    };

    // Placement ghosts and hover follow the pointer and leave with it.
    const onPointerLeave = () => {
      overlay.set({ ghost: null });
      setHover(null);
    };

    const onWheel = (event: WheelEvent) => {
      event.preventDefault();
      // Over a door or window marker the wheel resizes it instead of panning.
      const rawHandle = context.hitHandle(
        context.elementAt({ x: event.clientX, y: event.clientY })
      );
      const geometry = rawHandle ? parseGeometryHandle(rawHandle) : null;
      if (geometry?.kind === "opening") {
        const selected = store.getState().selection[0];
        if (
          selected &&
          resizeOpening(context, selected, geometry, event.deltaY > 0 ? -1 : 1)
        ) {
          return;
        }
      }
      const rect = svg.getBoundingClientRect();
      const pointer = {
        x: event.clientX - rect.left,
        y: event.clientY - rect.top
      };
      const current = liveViewport;
      const isZoom =
        isModifierKey(event) || store.getState().tool.kind === "pan";
      if (isZoom) {
        const factor = event.deltaY < 0 ? 1.1 : 1 / 1.1;
        const scale = Math.min(
          Math.max(current.scale * factor, MIN_SCALE),
          MAX_SCALE
        );
        if (scale === current.scale) return;
        const worldX = (pointer.x - current.x) / current.scale;
        const worldY = (pointer.y - current.y) / current.scale;
        applyViewport({
          x: pointer.x - worldX * scale,
          y: pointer.y - worldY * scale,
          scale
        });
      } else {
        // Trackpad panning; slower when zoomed in, like the Konva editor.
        const intensity = 1 / Math.max(current.scale, 0.5);
        applyViewport(
          panBy(current, {
            x: -event.deltaX * intensity,
            y: -event.deltaY * intensity
          })
        );
      }
      if (wheelTimer !== null) window.clearTimeout(wheelTimer);
      wheelTimer = window.setTimeout(commitViewport, WHEEL_COMMIT_DELAY);
    };

    const onKeyDown = (event: KeyboardEvent) => {
      // Shortcuts work wherever focus is (toolbar buttons included) but
      // never steal keys from a text field.
      const target = event.target as HTMLElement | null;
      if (target && isEditableElement(target)) return;
      const state = store.getState();
      const tool = activeTool();

      if (event.key === " " && !isSpaceDown) {
        isSpaceDown = true;
        event.preventDefault();
        context.setCursor("grab");
        return;
      }
      if (tool?.onKeyDown?.(context, event)) {
        event.preventDefault();
        return;
      }
      if (event.key === "Escape") {
        // The legacy deselect shortcut switches the tool; this only drops
        // the gesture in progress.
        tool?.cancel(context);
        return;
      }
      if (isModifierKey(event)) return;
      const nudge = arrowDelta(event.key, event.shiftKey ? NUDGE_FAST : NUDGE);
      if (nudge && state.selection.length > 0) {
        event.preventDefault();
        state.moveShapes(state.selection, nudge.x, nudge.y);
        return;
      }
    };

    const onKeyUp = (event: KeyboardEvent) => {
      if (event.key === " ") {
        isSpaceDown = false;
        if (!pan) context.setCursor(null);
      }
    };

    const onBlur = () => {
      isSpaceDown = false;
      activeTool()?.cancel(context);
    };

    // Right-click: the chrome's context menu opens for the shape under the
    // pointer, unless a multi-click drawing is in progress (right-click
    // finishes it there).
    const onContextMenu = (event: MouseEvent) => {
      if (activeTool()?.isBusy?.()) {
        event.preventDefault();
        return;
      }
      const id = context.hitShape(
        context.elementAt({ x: event.clientX, y: event.clientY })
      );
      const shape = id ? store.getState().shapes[id] : null;
      store.getState().setChrome({ contextMenuShape: shape ?? null });
    };

    svg.addEventListener("pointerdown", onPointerDown);
    svg.addEventListener("pointermove", onPointerMove);
    svg.addEventListener("pointerup", onPointerUp);
    svg.addEventListener("pointercancel", onPointerUp);
    svg.addEventListener("dblclick", onDoubleClick);
    svg.addEventListener("pointerleave", onPointerLeave);
    svg.addEventListener("wheel", onWheel, { passive: false });
    svg.addEventListener("contextmenu", onContextMenu);
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);
    window.addEventListener("blur", onBlur);

    // Tool switches cancel the outgoing tool's gesture and update the cursor.
    let previousKind = store.getState().tool.kind;
    const unsubscribe = store.subscribe((state) => {
      if (state.viewport !== liveViewport && !pan && wheelTimer === null) {
        liveViewport = state.viewport;
      }
      if (state.tool.kind !== previousKind) {
        tools[previousKind]?.cancel(context);
        previousKind = state.tool.kind;
        context.setCursor(null);
      }
    });
    context.setCursor(null);

    return () => {
      unsubscribe();
      if (wheelTimer !== null) window.clearTimeout(wheelTimer);
      svg.removeEventListener("pointerdown", onPointerDown);
      svg.removeEventListener("pointermove", onPointerMove);
      svg.removeEventListener("pointerup", onPointerUp);
      svg.removeEventListener("pointercancel", onPointerUp);
      svg.removeEventListener("dblclick", onDoubleClick);
      svg.removeEventListener("pointerleave", onPointerLeave);
      svg.removeEventListener("wheel", onWheel);
      svg.removeEventListener("contextmenu", onContextMenu);
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
      window.removeEventListener("blur", onBlur);
    };
  }, [store, overlay, tools, svgRef, rootRef, containerRef]);
};

const arrowDelta = (key: string, step: number): Point | null => {
  switch (key) {
    case "ArrowUp":
      return { x: 0, y: -step };
    case "ArrowDown":
      return { x: 0, y: step };
    case "ArrowLeft":
      return { x: -step, y: 0 };
    case "ArrowRight":
      return { x: step, y: 0 };
    default:
      return null;
  }
};

const isEditableElement = (element: HTMLElement): boolean =>
  element.tagName === "INPUT" ||
  element.tagName === "TEXTAREA" ||
  element.tagName === "SELECT" ||
  element.isContentEditable;
